import React, { useCallback, useEffect, useRef, useState } from "react";
import { Download, X, Loader2, CheckCircle2, Folder } from "lucide-react";
import { toast } from "sonner";
import { downloadProjectAssets, supportsFolderDownload } from "@/lib/downloadFolder";

const BACKEND = process.env.REACT_APP_BACKEND_URL;

function fmtBytes(n) {
    if (!n) return "0 B";
    if (n < 1024) return `${n} B`;
    if (n < 1024 ** 2) return `${(n / 1024).toFixed(1)} KB`;
    if (n < 1024 ** 3) return `${(n / 1024 ** 2).toFixed(1)} MB`;
    return `${(n / 1024 ** 3).toFixed(2)} GB`;
}

/**
 * Hook for streaming ZIP downloads with progress + cancel.
 *
 * Uses fetch + ReadableStream so we can show bytes downloaded as they arrive.
 * The backend uses zipstream-ng which doesn't send a Content-Length header,
 * so progress is indeterminate (we show downloaded bytes instead of a %).
 */
export function useZipDownload() {
    const [state, setState] = useState({
        active: false,
        filename: "",
        bytes: 0,
        done: false,
        error: null,
    });
    const abortRef = useRef(null);

    const cancel = useCallback(() => {
        if (abortRef.current) {
            try { abortRef.current.abort(); } catch { /* noop */ }
            abortRef.current = null;
        }
        setState({ active: false, filename: "", bytes: 0, done: false, error: null });
    }, []);

    const start = useCallback(async (path, filename) => {
        // Show an explicit "starting" popup so the user understands something is
        // happening — building a multi-photo ZIP server-side can take 5-15s
        // before the browser's own download bar appears.
        setState({ active: true, filename, bytes: 0, done: false, error: null, startedAt: Date.now() });

        const token = (() => { try { return localStorage.getItem("vsf_token") || ""; } catch { return ""; } })();
        const baseUrl = path.startsWith("http") ? path : `${BACKEND}/api${path.startsWith("/") ? "" : "/"}${path}`;
        const sep = baseUrl.includes("?") ? "&" : "?";
        const url = token ? `${baseUrl}${sep}token=${encodeURIComponent(token)}` : baseUrl;

        // Trigger the download via a normal anchor click — browser sees
        // Content-Disposition: attachment on the response and saves the file.
        const a = document.createElement("a");
        a.href = url;
        a.download = filename || "download.zip";
        a.style.display = "none";
        document.body.appendChild(a);
        a.click();
        a.remove();

        // Auto-close the popup after 15s — long enough for the server to build
        // the archive and Chrome's own download manager to take over the UI.
        setTimeout(() => setState({ active: false, filename: "", bytes: 0, done: false, error: null }), 15000);
    }, []);

    return { state, start, cancel };
}

export default function ZipDownloadOverlay({ state, onCancel }) {
    const [elapsed, setElapsed] = useState(0);
    useEffect(() => {
        if (!state.active || !state.startedAt) return undefined;
        const tick = () => setElapsed(Math.floor((Date.now() - state.startedAt) / 1000));
        tick();
        const id = setInterval(tick, 500);
        return () => clearInterval(id);
    }, [state.active, state.startedAt]);

    if (!state.active) return null;
    const remaining = Math.max(0, 15 - elapsed);
    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="zip-download-overlay">
            <div className="bg-[#FFFFFF] border border-zinc-200 rounded-md max-w-md w-full p-6 lg:p-8">
                <div className="flex items-start justify-between gap-3 mb-5">
                    <div className="flex items-center gap-3">
                        <Loader2 className="h-6 w-6 text-[#E5A93D] animate-spin flex-shrink-0" />
                        <div>
                            <h3 className="font-display font-bold text-lg" data-testid="zip-overlay-title">
                                ZIP wordt klaargemaakt
                            </h3>
                            <p className="text-xs text-zinc-500 mt-0.5 truncate max-w-[260px]">{state.filename}</p>
                        </div>
                    </div>
                    <button
                        type="button"
                        onClick={onCancel}
                        className="text-zinc-500 hover:text-zinc-950 p-1"
                        data-testid="zip-overlay-close"
                        aria-label="Sluit melding"
                    >
                        <X className="h-4 w-4" />
                    </button>
                </div>

                {/* Big friendly message — this is what the user came here to see */}
                <div className="bg-[#E5A93D]/5 border border-[#E5A93D]/20 rounded-sm p-4 text-center">
                    <Download className="h-5 w-5 text-[#E5A93D] mx-auto mb-2" />
                    <div className="text-zinc-950 font-semibold text-base" data-testid="zip-overlay-countdown">
                        {remaining > 0
                            ? <>Download start binnen <span className="text-[#E5A93D]">{remaining}s</span></>
                            : "Download zou nu moeten starten…"}
                    </div>
                    <p className="text-xs text-zinc-500 mt-1.5">
                        De server bundelt alle bestanden — even geduld.
                    </p>
                </div>

                {/* Indeterminate animated bar */}
                <div className="relative h-1.5 bg-[#F4F4F5] rounded-sm overflow-hidden border border-zinc-100 mt-4">
                    <div className="absolute inset-y-0 w-1/3 bg-[#E5A93D] zip-progress-marquee" />
                </div>

                {state.error && (
                    <div className="mt-4 text-xs text-red-700 bg-red-500/10 border border-red-500/30 rounded-sm p-2">
                        {state.error}
                    </div>
                )}

                <p className="text-[11px] text-zinc-500 mt-4 leading-relaxed">
                    Zodra je download verschijnt onderaan je browser kan je dit venster sluiten en gewoon verder werken.
                </p>
            </div>

            <style>{`
                @keyframes zip-progress-marquee {
                    0% { left: -33%; }
                    100% { left: 100%; }
                }
                .zip-progress-marquee { animation: zip-progress-marquee 1.2s linear infinite; }
            `}</style>
        </div>
    );
}


/**
 * Hook for folder-style downloads — grabs every video/photo for a project
 * one-by-one and writes them into a folder named after the project address.
 *
 * On Chromium browsers we use the File System Access API so the user actually
 * sees a real folder being created. On Safari/Firefox we trigger individual
 * downloads with a prefixed filename so they group in the Downloads folder.
 */
export function useFolderDownload() {
    const [state, setState] = useState({
        active: false,
        folderName: "",
        currentFile: "",
        done: 0,
        total: 0,
        completed: false,
        error: null,
    });

    const start = useCallback(async ({ folderName, files }) => {
        if (!files || files.length === 0) {
            toast.info("Geen bestanden om te downloaden");
            return;
        }
        setState({ active: true, folderName, currentFile: "", done: 0, total: files.length, completed: false, error: null });
        try {
            const { method } = await downloadProjectAssets({
                folderName,
                files,
                onProgress: ({ done, total, currentFile }) => {
                    setState((s) => (s.active ? { ...s, done, total, currentFile } : s));
                },
            });
            setState((s) => ({ ...s, completed: true, currentFile: "" }));
            toast.success(`${files.length} bestanden gedownload naar je Downloads-map`);
            setTimeout(() => setState((s) => (s.completed ? { active: false, folderName: "", currentFile: "", done: 0, total: 0, completed: false, error: null } : s)), 2000);
        } catch (err) {
            setState((s) => ({ ...s, error: err.message || "Mislukt" }));
            toast.error(err.message || "Download mislukt");
        }
    }, []);

    const cancel = useCallback(() => {
        setState({ active: false, folderName: "", currentFile: "", done: 0, total: 0, completed: false, error: null });
    }, []);

    return { state, start, cancel, supportsFolder: supportsFolderDownload() };
}

export function FolderDownloadOverlay({ state, onCancel }) {
    if (!state.active) return null;
    const pct = state.total > 0 ? Math.round((state.done / state.total) * 100) : 0;
    return (
        <div className="fixed inset-0 z-50 bg-black/70 backdrop-blur-sm flex items-center justify-center p-4" data-testid="folder-download-overlay">
            <div className="bg-[#FFFFFF] border border-zinc-200 rounded-md max-w-md w-full p-6 lg:p-8">
                <div className="flex items-start justify-between gap-3 mb-5">
                    <div className="flex items-center gap-3">
                        {state.completed ? (
                            <CheckCircle2 className="h-6 w-6 text-emerald-600 flex-shrink-0" />
                        ) : (
                            <Folder className="h-6 w-6 text-[#E5A93D] flex-shrink-0" />
                        )}
                        <div>
                            <h3 className="font-display font-bold text-lg" data-testid="folder-overlay-title">
                                {state.completed ? "Alle bestanden gedownload" : "Bestanden downloaden"}
                            </h3>
                            <p className="text-xs text-zinc-500 mt-0.5 truncate max-w-[260px]">{state.folderName}</p>
                        </div>
                    </div>
                    {!state.completed && (
                        <button
                            type="button"
                            onClick={onCancel}
                            className="text-zinc-500 hover:text-zinc-950 p-1"
                            data-testid="folder-overlay-cancel"
                            aria-label="Annuleer download"
                        >
                            <X className="h-4 w-4" />
                        </button>
                    )}
                </div>

                <div className="relative h-1.5 bg-[#F4F4F5] rounded-sm overflow-hidden border border-zinc-100">
                    <div
                        className={`absolute inset-y-0 left-0 transition-[width] duration-300 ${state.completed ? "bg-emerald-400" : "bg-[#E5A93D]"}`}
                        style={{ width: `${pct}%` }}
                    />
                </div>
                <div className="flex items-center justify-between mt-3 text-xs">
                    <div className="text-zinc-500">
                        <Download className="inline h-3 w-3 mr-1 align-text-bottom" />
                        <span className="font-mono text-zinc-950" data-testid="folder-overlay-progress">{state.done}/{state.total}</span>
                        <span className="text-zinc-500"> bestanden</span>
                    </div>
                    <div className="text-zinc-500 truncate ml-3 max-w-[180px]" title={state.currentFile}>
                        {state.currentFile && !state.completed ? state.currentFile : ""}
                    </div>
                </div>

                {state.error && (
                    <div className="mt-4 text-xs text-red-700 bg-red-500/10 border border-red-500/30 rounded-sm p-2">
                        {state.error}
                    </div>
                )}

                {!state.completed && (
                    <p className="text-[11px] text-zinc-500 mt-4 leading-relaxed">
                        Chrome vraagt mogelijk om meerdere downloads toe te staan — klik <strong className="text-zinc-600">Toestaan</strong>.
                    </p>
                )}
            </div>
        </div>
    );
}

